import React, { useState } from 'react';
import { toast as message } from 'sonner';
import { supabaseClient } from '../../../utils/supabase';
import type { ActionDialogProps } from '../types';
import { useQueryClient } from '@tanstack/react-query';
import EnhancedDialog from '../../EnhancedDialog';

export const AchRejectDialog: React.FC<ActionDialogProps> = ({
  isOpen,
  onClose,
  prefill,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const handleReject = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);

    try {
      const { data, error } = await supabaseClient.functions.invoke('ach-charge-reject', {
        body: {
          ach_charge_attempt_id: Number(prefill.ach_charge_attempt_id),
          rejection_reason: reason.trim(),
        },
      });

      if (data?.error) throw new Error(data.error);
      if (error) throw error;

      message.warning('Charge rejected');
      setReason('');
      onClose();
      queryClient.invalidateQueries({ queryKey: ['action-visibility'] });
      queryClient.invalidateQueries({ predicate: (q) => {
        const key = q.queryKey;
        return Array.isArray(key) && key.some((k) => typeof k === 'string' && (
          k.includes('ach_charge_attempt') || k.includes('ach_mandate') || k.includes('ach_customer')
        ));
      }});
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to reject charge';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setReason('');
      onClose();
    }
  };

  return (
    <EnhancedDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Reject ACH Charge"
      style={{ width: 450 }}
      canOutsideClickClose={!submitting}
    >
      <div className="sky-dialog-body">
        <div className="sky-callout sky-callout-warning" style={{ marginBottom: 12 }}>
          Rejecting this charge is final. The submitter will need to create a new charge if needed.
        </div>

        <div className="sky-form-group">
          <label className="sky-form-label">Rejection Reason <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
          <textarea
            className="sky-field-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this charge is being rejected..."
            rows={3}
            style={{ resize: 'vertical' }}
          />
        </div>
      </div>

      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" onClick={handleClose} disabled={submitting}>Cancel</button>
        <button
          className="sky-btn sky-btn-danger"
          onClick={handleReject}
          disabled={submitting || !reason.trim()}
        >
          {submitting ? <span className="sky-spinner" /> : null}
          Reject Charge
        </button>
      </div>
    </EnhancedDialog>
  );
};
